import React from "react";
import {DamageCarrier, Monster, MonsterDefensiveAbility, MonsterOffensiveAbility} from "../../../server/monster";

interface Props {
    monster: Monster;
    carriers: DamageCarrier[];
    /** When provided, renders only the requested side (suppresses pageBreakAfter for bulk grid use). */
    side?: "front" | "back";
}

const border = "1px solid #000";
const cell: React.CSSProperties = {border, padding: "1px 3px", fontSize: 7, verticalAlign: "middle"};
const hdr: React.CSSProperties = {...cell, fontWeight: "bold", textAlign: "center"};
const cardStyle: React.CSSProperties = {
    width: "5in", height: "3in", boxSizing: "border-box", border: "2px solid #000",
    fontFamily: "Arial, sans-serif", fontSize: 7, display: "flex", flexDirection: "column",
    padding: 0, overflow: "hidden", pageBreakAfter: "always",
};
const cardStyleNoBreak: React.CSSProperties = {...cardStyle, pageBreakAfter: "auto"};

/** Renders one or two 5"×3" index cards for printing. */
export function MonsterIndexCard({monster, carriers, side}: Props) {
    const attrs = monster.attributes ?? {};
    const intelligence = monster.intelligence;
    const offAbilities = monster.offensiveAbilities ?? [];
    const defAbilities = monster.defensiveAbilities ?? [];

    const seenIds = new Set<number>();
    const monstrous = [
        ...(monster.type?.species?.monstrousAbilities ?? []),
        ...(monster.type?.monstrousAbilities ?? []),
        ...(monster.monstrousAbilities ?? []),
    ].filter(e => {
        const id = e.ability.id as number;
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
    });

    const typeName = monster.type?.name ?? "";
    const carrierName = carriers.find(c => c.id === attrs.carrierId)?.name ?? "";
    const damageLabel = attrs.damage != null
        ? `${attrs.damage}${carrierName ? ` ${carrierName}` : ""}`
        : "";

    const offAbilitiesSorted = [...offAbilities].sort((a, b) => a.ability.name.localeCompare(b.ability.name));
    const defAbilitiesSorted = [...defAbilities].sort((a, b) => a.ability.name.localeCompare(b.ability.name));

    function pad<T>(arr: T[], min: number): (T | null)[] {
        return arr.length >= min ? arr : [...arr, ...Array(min - arr.length).fill(null)];
    }

    const typeOrder: Record<string, number> = {WEAKNESS: 0, STRENGTH: 1, IMMUNITY: 2};
    const sortedMonstrous = [...monstrous].sort((a, b) =>
        (typeOrder[a.ability.type] ?? 3) - (typeOrder[b.ability.type] ?? 3)
    );
    const monActualCount = sortedMonstrous.length;
    const monHalf = Math.ceil(monActualCount / 2);
    const monLeft = pad(sortedMonstrous.slice(0, monHalf), Math.max(2, monHalf));
    const monRight = pad(sortedMonstrous.slice(monHalf), Math.max(2, monHalf));
    const monFontSize = Math.min(15, 16 - Math.max(2, monHalf));

    const needsDoubleSided = offAbilitiesSorted.length > 6 || defAbilitiesSorted.length > 6 || monActualCount > 8;

    // ── Icon helpers ──────────────────────────────────────────────────────────

    function OffIcon({label, children}: {label: string, children: React.ReactNode}) {
        return (
            <svg aria-label={label} width="9" height="9" viewBox="0 0 10 10"
                 style={{display: "inline-block", verticalAlign: "middle", marginRight: 1, flexShrink: 0}}>
                {children}
            </svg>
        );
    }

    function BombIcon() {
        return <OffIcon label="Packet Only">
            <circle cx="5" cy="6.5" r="3.5" fill="black"/>
            <line x1="5" y1="3" x2="8" y2="0.5" stroke="black" strokeWidth="1.2" strokeLinecap="round"/>
            <circle cx="8.5" cy="0.5" r="0.8" fill="black"/>
        </OffIcon>;
    }

    function HandIcon() {
        return <OffIcon label="Touchcast">
            <rect x="1.5" y="2" width="1.2" height="4" rx="0.6" fill="black"/>
            <rect x="3.2" y="1" width="1.2" height="4.5" rx="0.6" fill="black"/>
            <rect x="4.9" y="1" width="1.2" height="4.5" rx="0.6" fill="black"/>
            <rect x="6.6" y="2" width="1.2" height="4" rx="0.6" fill="black"/>
            <rect x="1.5" y="5" width="6.3" height="4" rx="1" fill="black"/>
        </OffIcon>;
    }

    function PersonIcon() {
        return <OffIcon label="Selfcast">
            <circle cx="5" cy="2.5" r="2" fill="black"/>
            <path d="M1.5,10 Q1.5,5.5 5,5.5 Q8.5,5.5 8.5,10 Z" fill="black"/>
        </OffIcon>;
    }

    function SwordIcon() {
        return <OffIcon label="Strike">
            <line x1="2" y1="9" x2="8.5" y2="1" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="1.5" y1="5.5" x2="5" y2="8.5" stroke="black" strokeWidth="1.2" strokeLinecap="round"/>
        </OffIcon>;
    }

    function EyeIcon() {
        return <OffIcon label="Gaze">
            <path d="M1,5 Q5,0.5 9,5 Q5,9.5 1,5 Z" fill="black"/>
            <circle cx="5" cy="5" r="2" fill="white"/>
            <circle cx="5" cy="5" r="1.2" fill="black"/>
        </OffIcon>;
    }

    function OffensiveIcons({o}: {o: MonsterOffensiveAbility}) {
        return <>
            {o.packet_only && <BombIcon/>}
            {o.touchcast && <HandIcon/>}
            {o.selfcast && <PersonIcon/>}
            {o.strike && <SwordIcon/>}
            {o.gaze && <EyeIcon/>}
        </>;
    }

    // ── Format helpers ────────────────────────────────────────────────────────

    function formatDefCall(d: MonsterDefensiveAbility): string {
        let text = d.ability.name;
        if (d.swarm != null) text = `SWARM ${d.swarm}: ${text}`;
        if (d.triggered_by) text = `${text} triggered by ${d.triggered_by}`;
        if (d.conditional_on) text = `${text} Conditional on: ${d.conditional_on}`;
        return text;
    }

    function formatOffCall(o: MonsterOffensiveAbility): string {
        let call = o.ability.call.replace(/<carrier>/gi, carrierName);
        if (o.swarm != null) call = `SWARM ${o.swarm}: ${call}`;
        if (o.triggered_by) call = `${call} triggered by ${o.triggered_by}`;
        if (o.conditional_on) call = `${call} Conditional on: ${o.conditional_on}`;
        return call;
    }

    // ── Shared section renderers ──────────────────────────────────────────────

    function Header() {
        return (
            <table style={{width: "100%", borderCollapse: "collapse", tableLayout: "fixed"}}>
                <colgroup>
                    <col style={{width: "28%"}}/><col style={{width: "10%"}}/><col style={{width: "10%"}}/>
                    <col style={{width: "12%"}}/><col style={{width: "24%"}}/><col style={{width: "16%"}}/>
                </colgroup>
                <tbody>
                <tr>
                    <td rowSpan={2} style={{...cell, fontSize: 9, fontWeight: "bold", verticalAlign: "middle"}}>
                        {monster.name}{typeName ? ` (${typeName})` : ""}
                    </td>
                    <td style={hdr}>ARMOR</td>
                    <td style={hdr}>BODY</td>
                    <td style={hdr}>DAMAGE</td>
                    <td style={{...cell, fontSize: 7}}>IQ : {intelligence?.name ?? ""}</td>
                    <td style={{...cell, fontSize: 7}}>Date:</td>
                </tr>
                <tr>
                    <td style={{...cell, textAlign: "center", height: 14, fontSize: 12}}>{attrs.armor ?? ""}</td>
                    <td style={{...cell, textAlign: "center", fontSize: 12}}>{attrs.body ?? ""}</td>
                    <td style={{...cell, textAlign: "center", fontSize: 10}}>{damageLabel}</td>
                    <td style={{...cell, fontSize: 7}}>Weapons : {attrs.weaponry ?? ""}</td>
                    <td style={{...cell, fontSize: 7}}>Validated:</td>
                </tr>
                </tbody>
            </table>
        );
    }

    function MonstrousSection() {
        const fmtMon = (entry: typeof monLeft[0]) =>
            entry ? `${entry.ability.name} — ${entry.ability.call}` : "";
        return <>
            <table style={{width: "100%", borderCollapse: "collapse", tableLayout: "fixed"}}>
                <tbody>
                <tr>
                    <td colSpan={3} style={{...hdr, borderTop: "1px solid black", fontSize: 7}}>
                        Monsterous Abilities
                    </td>
                </tr>
                </tbody>
            </table>
            <table style={{width: "100%", borderCollapse: "collapse", tableLayout: "fixed"}}>
                <colgroup>
                    <col style={{width: "49%"}}/><col style={{width: "2%"}}/><col style={{width: "49%"}}/>
                </colgroup>
                <tbody>
                {monLeft.map((m, i) => (
                    <tr key={i}>
                        <td style={{...cell, fontSize: monFontSize}}>{fmtMon(m)}</td>
                        <td style={{borderLeft: "1px solid #000"}}/>
                        <td style={{...cell, fontSize: monFontSize}}>{fmtMon(monRight[i] ?? null)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </>;
    }

    // ── Single-sided layout ───────────────────────────────────────────────────

    if (!needsDoubleSided) {
        const abilityCount = Math.max(defAbilitiesSorted.length, offAbilitiesSorted.length, 1);
        const abilityRowCount = Math.max(abilityCount, 6);
        const offRows = pad(offAbilitiesSorted, abilityRowCount);
        const defRows = pad(defAbilitiesSorted, abilityRowCount);
        const abilityFontSize = Math.min(18, 18 - abilityCount);

        return (
            <div style={side ? cardStyleNoBreak : cardStyle}>
                <Header/>
                <table style={{width: "100%", borderCollapse: "collapse", tableLayout: "fixed", flex: 1}}>
                    <colgroup>
                        <col style={{width: "37%"}}/><col style={{width: "9%"}}/><col style={{width: "1%"}}/>
                        <col style={{width: "35%"}}/><col style={{width: "9%"}}/><col style={{width: "9%"}}/>
                    </colgroup>
                    <tbody>
                    <tr style={{height: 13}}>
                        <td style={{...hdr, fontSize: 7, padding: "1px 3px"}}>Defense</td>
                        <td style={{...hdr, fontSize: 7, padding: "1px 3px"}}>Times</td>
                        <td style={{borderLeft: "1px solid #000"}}/>
                        <td style={{...hdr, fontSize: 7, padding: "1px 3px"}}>Offense</td>
                        <td style={{...hdr, fontSize: 7, padding: "1px 3px"}}>Recharge</td>
                        <td style={{...hdr, fontSize: 7, padding: "1px 3px"}}>Times</td>
                    </tr>
                    {defRows.map((d, i) => {
                        const o = offRows[i] ?? null;
                        return (
                            <tr key={i}>
                                <td style={{...cell, fontSize: abilityFontSize}}>{d ? formatDefCall(d) : ""}</td>
                                <td style={{...cell, fontSize: abilityFontSize, textAlign: "center"}}>{d ? d.quantity : ""}</td>
                                <td style={{borderLeft: "1px solid #000"}}/>
                                <td style={{...cell, fontSize: abilityFontSize}}>
                                    {o ? <><OffensiveIcons o={o}/>{formatOffCall(o)}</> : ""}
                                </td>
                                <td style={{...cell, fontSize: abilityFontSize, textAlign: "center"}}>{o ? (o.recharge_time > 0 ? `${o.recharge_time}s` : "") : ""}</td>
                                <td style={{...cell, fontSize: abilityFontSize, textAlign: "center"}}>{o ? o.quantity : ""}</td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
                <MonstrousSection/>
            </div>
        );
    }

    // ── Double-sided layout ───────────────────────────────────────────────────

    // Front: offense in two columns
    const offHalf = Math.ceil(offAbilitiesSorted.length / 2);
    const offRowCount = Math.min(8, offHalf);
    const offLeftRows = pad(offAbilitiesSorted.slice(0, offHalf), offRowCount);
    const offRightRows = pad(offAbilitiesSorted.slice(offHalf), offRowCount);
    const offFrontFontSize = Math.min(18, 24 - offHalf);

    // Back: defense in two columns
    const defHalf = Math.ceil(defAbilitiesSorted.length / 2);
    const defRowCount = Math.min(8, defHalf);
    const defLeftRows = pad(defAbilitiesSorted.slice(0, defHalf), defRowCount);
    const defRightRows = pad(defAbilitiesSorted.slice(defHalf), defRowCount);
    const defBackFontSize = Math.min(18, 21 - defHalf);

    const offCell = (o: MonsterOffensiveAbility | null) => o
        ? <><OffensiveIcons o={o}/>{formatOffCall(o)}</>
        : "";

    const cs = side ? cardStyleNoBreak : cardStyle;

    const frontCard = (
        <div style={cs}>
            <Header/>
            <div style={{flex: 1, overflow: "hidden", display: "flex", flexDirection: "column"}}>
            <table style={{width: "100%", height: "100%", borderCollapse: "collapse", tableLayout: "fixed"}}>
                <colgroup>
                    <col style={{width: "35%"}}/><col style={{width: "8%"}}/><col style={{width: "6%"}}/>
                    <col style={{width: "2%"}}/>
                    <col style={{width: "35%"}}/><col style={{width: "8%"}}/><col style={{width: "6%"}}/>
                </colgroup>
                <tbody>
                <tr style={{height: 13}}>
                    <td style={{...hdr, fontSize: 7, padding: "1px 2px"}}>Offense</td>
                    <td style={{...hdr, fontSize: 7, padding: "1px 2px"}}>Recharge</td>
                    <td style={{...hdr, fontSize: 7, padding: "1px 2px"}}>Times</td>
                    <td style={{borderLeft: "1px solid #000"}}/>
                    <td style={{...hdr, fontSize: 7, padding: "1px 2px"}}>Offense (cont.)</td>
                    <td style={{...hdr, fontSize: 7, padding: "1px 2px"}}>Recharge</td>
                    <td style={{...hdr, fontSize: 7, padding: "1px 2px"}}>Times</td>
                </tr>
                {offLeftRows.map((ol, i) => {
                    const or = offRightRows[i] ?? null;
                    return (
                        <tr key={i}>
                            <td style={{...cell, fontSize: offFrontFontSize}}>{offCell(ol)}</td>
                            <td style={{...cell, fontSize: offFrontFontSize, textAlign: "center"}}>{ol ? (ol.recharge_time > 0 ? `${ol.recharge_time}s` : "") : ""}</td>
                            <td style={{...cell, fontSize: offFrontFontSize, textAlign: "center"}}>{ol ? ol.quantity : ""}</td>
                            <td style={{borderLeft: "1px solid #000"}}/>
                            <td style={{...cell, fontSize: offFrontFontSize}}>{offCell(or)}</td>
                            <td style={{...cell, fontSize: offFrontFontSize, textAlign: "center"}}>{or ? (or.recharge_time > 0 ? `${or.recharge_time}s` : "") : ""}</td>
                            <td style={{...cell, fontSize: offFrontFontSize, textAlign: "center"}}>{or ? or.quantity : ""}</td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
            </div>
        </div>
    );

    const backCard = (
        <div style={cs}>
            <Header/>
            <div style={{flex: 1, overflow: "hidden", display: "flex", flexDirection: "column"}}>
            <table style={{width: "100%", height: "100%", borderCollapse: "collapse", tableLayout: "fixed"}}>
                <colgroup>
                    <col style={{width: "42%"}}/><col style={{width: "8%"}}/>
                    <col style={{width: "2%"}}/>
                    <col style={{width: "42%"}}/><col style={{width: "8%"}}/>
                </colgroup>
                <tbody>
                <tr style={{height: 13}}>
                    <td style={{...hdr, fontSize: 7, padding: "1px 3px"}}>Defense</td>
                    <td style={{...hdr, fontSize: 7, padding: "1px 3px"}}>Times</td>
                    <td style={{borderLeft: "1px solid #000"}}/>
                    <td style={{...hdr, fontSize: 7, padding: "1px 3px"}}>Defense (cont.)</td>
                    <td style={{...hdr, fontSize: 7, padding: "1px 3px"}}>Times</td>
                </tr>
                {defLeftRows.map((d, i) => {
                    const dr = defRightRows[i] ?? null;
                    return (
                        <tr key={i}>
                            <td style={{...cell, fontSize: defBackFontSize}}>{d ? formatDefCall(d) : ""}</td>
                            <td style={{...cell, fontSize: defBackFontSize, textAlign: "center"}}>{d ? d.quantity : ""}</td>
                            <td style={{borderLeft: "1px solid #000"}}/>
                            <td style={{...cell, fontSize: defBackFontSize}}>{dr ? formatDefCall(dr) : ""}</td>
                            <td style={{...cell, fontSize: defBackFontSize, textAlign: "center"}}>{dr ? dr.quantity : ""}</td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
            </div>
            <MonstrousSection/>
        </div>
    );

    if (side === "front") return frontCard;
    if (side === "back") return backCard;
    return <>{frontCard}{backCard}</>;
}
